<template>
  <div class="patient-form-container" :class="modalTheme">
    <!-- Add the load button at the top -->
    <div class="form-actions-top">
      <button :disabled="!patientStore.currentPatient.lastName" type="button" class="btn btn-info" @click="loadFromPatientStore">Load Current Patient from Store</button>
      <button type="button" class="btn btn-secondary" @click="loadFromProps">Load from Props</button>
      <button type="button" class="btn btn-secondary" @click="loadSampleData">Load Sample Data</button>
      <span v-if="patientStore.currentPatient.lastName" class="current-patient-info"> Current: {{ patientStore.currentPatient.firstName }} {{ patientStore.currentPatient.lastName }} </span>
      <span v-else class="no-patient-info"> No current patient selected </span>
    </div>

    <b-form @submit.prevent="onSubmit" @reset.prevent="onReset">
      <!-- Row 1: Legal Information and System Identifiers -->
      <b-row>
        <b-col cols="12" md="6">
          <!-- Legal Information Section -->
          <div class="form-section" :class="userStore.borderClass">
            <h3 style="text-align: left">Legal Name</h3>
            <b-form-group label="Title:" label-for="legalTitle" label-cols="3" label-size="sm" label-align-sm="right" class="mb-2">
              <b-form-input id="legalTitle" v-model="form.legalTitle" type="text" placeholder="Dr., Mr., Ms., etc." size="sm" :class="modalTheme" />
            </b-form-group>
            <b-form-group label="First Name:" label-for="legalFirstName" label-cols="3" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
              <b-form-input id="legalFirstName" v-model="form.legalFirstName" type="text" size="sm" :class="modalTheme" required />
            </b-form-group>
            <b-form-group label="Middle Name:" label-for="legalMiddleName" label-cols="3" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
              <b-form-input id="legalMiddleName" v-model="form.legalMiddleName" type="text" size="sm" :class="modalTheme" />
            </b-form-group>
            <b-form-group label="Last Name:" label-for="legalLastName" label-cols="3" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
              <b-form-input id="legalLastName" v-model="form.legalLastName" type="text" size="sm" :class="modalTheme" required />
            </b-form-group>
            <b-form-group label="Suffix:" label-for="legalSuffix" label-cols="3" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
              <b-form-input id="legalSuffix" v-model="form.legalSuffix" type="text" placeholder="Jr., Sr., III" size="sm" :class="modalTheme" />
            </b-form-group>


          </div>
        </b-col>
        <b-col cols="12" md="6">
          <!-- System Identifiers Section -->
          <div class="form-section identification-section" :class="userStore.borderClass">
            <h3 style="text-align: left">Identification</h3>
            <b-row class="mb-2">
              <b-col cols="3" class="d-flex align-items-center justify-content-end">
                <label for="idPatientNumber" class="mb-0 font-weight-bold">Patient #:</label>
              </b-col>
              <b-col cols="4">
                <b-form-input id="idPatientNumber" v-model="form.idPatientNumber" type="text" size="sm" :class="modalTheme" />
              </b-col>
              <b-col cols="5" class="d-flex align-items-center">
                <b-form-checkbox id="idTraining" v-model="form.idTraining" size="sm" :class="modalTheme">Training</b-form-checkbox>
              </b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col cols="3" class="d-flex align-items-center justify-content-end">
                <label for="idSocialSecurityNumber" class="mb-0 font-weight-bold">SSN:</label>
              </b-col>
              <b-col cols="4">
                <div class="ssn-input-container">
                  <b-form-input
                    id="idSocialSecurityNumber"
                    v-model="ssnDisplay"
                    type="text"
                    placeholder="XXX-XX-XXXX"
                    size="sm"
                    :class="modalTheme"
                    maxlength="11"
                    @input="handleSsnInput"
                    @blur="maskSsnDisplay"
                    @focus="showFullSsnOnFocus"
                    :state="ssnValidationState"
                  />
                  <!-- <b-button size="sm" variant="outline-secondary" class="ssn-toggle-btn" @click="toggleSsnVisibility" :title="showFullSSN ? 'Hide SSN' : 'Show SSN'">
                    <font-awesome-icon :icon="showFullSSN ? 'eye-slash' : 'eye'" />
                  </b-button> -->
                </div>

                <b-form-invalid-feedback :state="ssnValidationState"> Please enter a valid 9-digit SSN (###-##-####). </b-form-invalid-feedback>
              </b-col>
              <b-col cols="5" class="d-flex align-items-center">
                <b-form-checkbox id="idActive" v-model="form.idActive" size="sm" :class="modalTheme">Active</b-form-checkbox>
              </b-col>
            </b-row>

            <b-row class="mb-2">
              <b-col cols="3" class="d-flex align-items-center justify-content-end">
                <label for="idReferredBy" class="mb-0 font-weight-bold">Referred By:</label>
              </b-col>
              <b-col cols="9">
                <b-form-input id="idReferredBy" v-model="form.idReferredBy" type="text" size="sm" :class="modalTheme" />
              </b-col>
            </b-row>
            <b-row class="mb-2">
              <b-col cols="3" class="d-flex align-items-center justify-content-end">
                <label for="idUsualCaregiver" class="mb-0 font-weight-bold">Usual Caregiver:</label>
              </b-col>
              <b-col cols="9">
                <b-form-select
                  id="idUsualCaregiver"
                  v-model="form.idUsualCaregiver"
                  size="sm"
                  :options="[
                    { value: '', text: 'Select Caregiver (list will be DOCs)' },
                    { value: 'self', text: 'Self' },
                    { value: 'spouse', text: 'Spouse' },
                    { value: 'parent', text: 'Parent' },
                    { value: 'child', text: 'Child' },
                    { value: 'sibling', text: 'Sibling' },
                    { value: 'family_member', text: 'Family Member' },
                    { value: 'home_health_aide', text: 'Home Health Aide' },
                    { value: 'nurse', text: 'Nurse' },
                    { value: 'assisted_living', text: 'Assisted Living' },
                    { value: 'nursing_home', text: 'Nursing Home' },
                    { value: 'other', text: 'Other' },
                  ]"
                  :class="modalTheme"
                />
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>

            <!-- Row 1: Legal Information and System Identifiers -->
      <b-row>
        <b-col cols="12" md="6">
          <!-- Legal Information Section -->
          <div class="form-section" :class="userStore.borderClass">
            <h3 style="text-align: left">Home Address Information</h3>
            <b-form-group label="Address 1:" label-for="homeAddress1" label-cols="3" label-align="right" label-size="sm" label-class="form-label-align" class="mb-0">
              <b-form-input id="homeAddress1" :class="modalTheme" v-model="form.homeAddress1" size="sm" placeholder="Enter home address line 1"></b-form-input>
            </b-form-group>
            <b-form-group label="Address 2:" label-for="homeAddress2" label-cols="3" label-size="sm" label-align="right" label-class="form-label-align" class="mb-2">
              <b-form-input id="homeAddress2" :class="modalTheme" v-model="form.homeAddress2" type="text" size="sm" />
            </b-form-group>

            <!-- City/State row aligned with Address 1/2 -->
            <b-form-group class="mb-0" label-cols="1" label-class="form-label-align">
              <b-row no-gutters>
                <b-col cols="6" class="pr-1">
                  <b-form-group label="City:" label-for="homeCity" label-cols="4" label-align="right" label-size="sm" label-class="form-label-align" class="mb-0">
                    <b-form-input id="homeCity" :class="modalTheme" v-model="form.homeCity" size="sm" placeholder="Enter city"></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col cols="6" class="pl-1">
                  <b-form-group label="State:" label-for="homeState" label-cols="4" label-align="right" label-size="sm" label-class="form-label-align" class="mb-0">
                    <b-form-input id="homeState" :class="modalTheme" v-model="form.homeState" size="sm" placeholder="Enter state"></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-form-group>

            <b-form-group class="mb-0" label-cols="1" label-class="form-label-align">
              <b-row no-gutters>
                <b-col cols="6" class="pr-1">
                  <b-form-group label="County:" label-for="homeCounty" label-cols="4" label-align="right" label-size="sm" label-class="form-label-align" class="mb-0">
                    <b-form-input id="homeCounty" :class="modalTheme" v-model="form.homeCounty" size="sm" placeholder="Enter county"></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col cols="6" class="pl-1">
                  <b-form-group label="Zip Code:" label-for="homeZipCode" label-cols="4" label-align="right" label-size="sm" label-class="form-label-align" class="mb-0">
                    <b-form-input id="homeZipCode" :class="modalTheme" v-model="form.homeZipCode" size="sm" placeholder="Enter zip code"></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-form-group>

            <b-form-group label="Country:" label-for="homeCountry" label-cols="3" label-size="sm" label-align="right" label-class="form-label-align" class="mb-2">
              <b-form-input id="homeCountry" :class="modalTheme" v-model="form.homeCountry" type="text" size="sm" placeholder="Enter country" />
            </b-form-group>
          </div>

        </b-col>
        <b-col cols="12" md="6">
          <!-- Contact Information Section -->
          <div class="form-section identification-section" :class="userStore.borderClass">
            <h3 style="text-align: left">Contact Information</h3>

            <b-row class="mb-2">
              <b-col cols="3" class="d-flex align-items-center justify-content-end">
                <label for="homeTelephoneNumber" class="mb-0 font-weight-bold">Home Number:</label>
              </b-col>
              <b-col cols="4">
                <b-form-input
                  id="homeTelephoneNumber"
                  :class="modalTheme"
                  v-model="form.homeTelephoneNumber"
                  type="tel"
                  size="sm"
                  placeholder="Enter Home Telephone Number"
                  v-mask="'(###) ###-####'"
                />
              </b-col>
              <b-col cols="5" class="d-flex align-items-center">
                <b-form-checkbox id="homeTelephoneNumberCanText" v-model="form.homeTelephoneNumberCanText" size="sm">Supports Text Messages </b-form-checkbox>
              </b-col>
            </b-row>

            <b-row class="mb-2">
              <b-col cols="3" class="d-flex align-items-center justify-content-end">
                <label for="homeWorkNumber" class="mb-0 font-weight-bold">Work Number:</label>
              </b-col>
              <b-col cols="4">
                <b-form-input id="homeWorkNumber" :class="modalTheme" v-model="form.homeWorkNumber" type="tel" v-mask="'(###) ###-####'" size="sm" placeholder="Enter work number" />
              </b-col>
              <b-col cols="5" class="d-flex align-items-center">
                <b-form-checkbox id="homeWorkNumberCanText" v-model="form.homeWorkNumberCanText" size="sm">Supports Text Messages </b-form-checkbox>
              </b-col>
            </b-row>

            <b-row class="mb-2">
              <b-col cols="3" class="d-flex align-items-center justify-content-end">
                <label for="homeMobileNumber" class="mb-0 font-weight-bold">Mobile Number:</label>
              </b-col>
              <b-col cols="4">
                <b-form-input id="homeMobileNumber" :class="modalTheme" v-model="form.homeMobileNumber" type="tel" v-mask="'(###) ###-####'" size="sm" placeholder="Enter Mobile number" />
              </b-col>
              <b-col cols="5" class="d-flex align-items-center">
                <b-form-checkbox id="homeTelephoneNumberCanText" v-model="form.homeMobileNumberCanText" size="sm">Supports Text Messages </b-form-checkbox>
              </b-col>
            </b-row>

            <b-row class="mb-2">
              <b-col cols="3" class="d-flex align-items-center justify-content-end">
                <label for="homeTelephoneNumber" class="mb-0 font-weight-bold">Other Telephone:</label>
              </b-col>
              <b-col cols="4">
                <b-form-input id="homeOtherNumber" :class="modalTheme" v-model="form.homeOtherNumber" type="tel" v-mask="'(###) ###-####'" size="sm" placeholder="Enter Other number" />
              </b-col>
              <b-col cols="5" class="d-flex align-items-center">
                <b-form-checkbox id="homeTelephoneNumberCanText" v-model="form.homeOtherNumberCanText" size="sm">Supports Text Messages </b-form-checkbox>
              </b-col>
            </b-row>

            <b-form-group label="Fax Number:" label-for="homeFaxNumber" label-cols="3" label-align="right" label-size="sm" label-class="form-label-align" class="mb-0">
              <b-form-input id="homeFaxNumber" :class="modalTheme" v-model="form.homeFaxNumber" type="tel" v-mask="'(###) ###-####'" placeholder="Enter fax number" size="sm"></b-form-input>
            </b-form-group>

            <b-form-group label="Pager Number:" label-for="homePagerNumber" label-cols="3" label-align="right" label-size="sm" label-class="form-label-align" class="mb-0">
              <b-form-input id="homePagerNumber" :class="modalTheme" v-model="form.homePagerNumber" type="tel" v-mask="'(###) ###-####'" placeholder="Enter pager number" size="sm"></b-form-input>
            </b-form-group>
          </div>
        </b-col>
      </b-row>
      <div class="form-section full-width" :class="userStore.borderClass">
        <!-- <h3>Additional Information</h3> -->
        <div class="form-row">
          <div class="inline-group" style="width: 100%">
            <b-form-group label="Email Address:" label-for="homeEmailAdress" label-cols="3" label-align="right" label-class="form-label-align" class="mb-0">
              <b-form-input id="homeEmailAdress" :class="modalTheme" v-model="form.homeEmailAdress" type="email" placeholder="Enter email address"></b-form-input>
            </b-form-group>
          </div>
        </div>
      </div>


      <!-- Row 2: Privacy & Security and Birth Information -->
      <b-row v-if="!AreIdsShown">
        <b-col cols="12">
          <!-- Privacy & Security Section -->
          <div class="form-section" :class="userStore.borderClass">
            <h3 style="text-align: left">Privacy & Security</h3>

            <b-row class="mb-2">
              <b-col cols="2" class="d-flex align-items-center justify-content-end">
                <label for="privacyLevel" class="mb-0 font-weight-bold">Privacy:</label>
              </b-col>
              <b-col cols="4">
                <b-form-select
                  id="privacyLevel"
                  v-model="form.privacyLevel"
                  size="sm"
                  :options="[
                    { value: '', text: 'Choose Level' },
                    { value: 'public', text: 'Public' },
                    { value: 'restricted', text: 'Restricted' },
                    { value: 'confidential', text: 'Confidential' },
                    { value: 'secret', text: 'Secret' },
                  ]"
                  :class="modalTheme"
                />
              </b-col>
              <b-col cols="6" class="d-flex align-items-center">
                <span class="privacy-info text-muted">Determines who can access the chart in the EHR</span>
              </b-col>
            </b-row>

            <b-row class="mb-2">
              <b-col cols="2" class="d-flex align-items-center justify-content-end">
                <label for="privacyConfidentialityLevel" class="mb-0 font-weight-bold">Confidentiality:</label>
              </b-col>
              <b-col cols="4">
                <b-form-select
                  id="privacyConfidentialityLevel"
                  v-model="form.privacyConfidentialityLevel"
                  size="sm"
                  :options="[
                    { value: '', text: 'Select Level' },
                    { value: 'low', text: 'Low' },
                    { value: 'medium', text: 'Medium' },
                    { value: 'high', text: 'High' },
                    { value: 'maximum', text: 'Maximum' },
                  ]"
                  :class="modalTheme"
                />
              </b-col>
              <b-col cols="6" class="d-flex align-items-center">
                <span class="confidentiality-info text-muted">Determines the access level of the exported data.</span>
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>

      <!-- Row 3: Demographics & Identity (full width) -->
      <b-row v-if="!AreIdsShown">
        <b-col cols="12">
          <!-- Demographics & Identity Section -->
          <div class="form-section" :class="userStore.borderClass">
            <h3 style="text-align: left">Demographics</h3>
            <b-row>
              <b-col cols="6">
                <div class="form-row inline-form">
                  <b-form-group label="Date Of Birth:" label-for="demoDateOfBirth" label-cols="4" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
                    <div class="d-flex align-items-center">
                      <b-form-input id="demoDateOfBirth" v-model="form.demoDateOfBirth" type="date" size="sm" :class="modalTheme" class="flex-grow-1 mr-2" />
                      <b-form-input id="demoDateOfBirthYearsAgo" v-model="form.demoDateOfBirthYearsAgo" type="text" readonly size="sm" :class="modalTheme" class="flex-shrink-0" style="width: 140px" />
                    </div>
                  </b-form-group>
                  <!-- <b-form-group label="Years Ago:" label-for="demoDateOfBirthYearsAgo" label-cols="4" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
                  </b-form-group> -->
                </div>

                <div class="form-row inline-form">
                  <b-form-group label-cols="4" class="mb-2" :class="modalTheme">
                    <b-form-checkbox id="demoHasTimeOfBirth" v-model="form.demoHasTimeOfBirth" size="sm" :class="modalTheme"> Has Time </b-form-checkbox>
                  </b-form-group>
                  <b-form-group label="Time:" label-for="demoTimeOfBirth" label-cols="4" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
                    <b-form-input id="demoTimeOfBirth" v-model="form.demoTimeOfBirth" type="time" :disabled="!form.demoHasTimeOfBirth" size="sm" :class="modalTheme" />
                  </b-form-group>
                  <b-form-group label="Single/Multiple Birth:" label-for="demoSingleMultiBirth" label-cols="4" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
                    <b-form-select
                      id="demoSingleMultiBirth"
                      v-model="form.demoSingleMultiBirth"
                      size="sm"
                      :options="[
                        { value: '', text: 'Select Type' },
                        { value: 'singleton', text: 'Singleton' },
                        { value: 'twin', text: 'Twin' },
                        { value: 'triplet', text: 'Triplet' },
                        { value: 'quadruplet', text: 'Quadruplet' },
                        { value: 'other', text: 'Other Multiple Birth' },
                      ]"
                      :class="modalTheme"
                    />
                  </b-form-group>
                  <b-form-group label="Birth Order:" label-for="demoBirthOrder" label-cols="4" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
                    <b-form-input id="demoBirthOrder" v-model="form.demoBirthOrder" type="number" min="1" size="sm" :class="modalTheme" />
                  </b-form-group>
                  <b-form-group label="Birth Sex:" label-for="demoBirthSex" label-cols="4" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
                    <b-form-select
                      id="demoBirthSex"
                      v-model="form.demoBirthSex"
                      size="sm"
                      :options="[
                        { value: '', text: 'Select' },
                        { value: 'F', text: 'Female' },
                        { value: 'M', text: 'Male' },
                        { value: 'I', text: 'Intersex' },
                        { value: 'U', text: 'Unknown' },
                      ]"
                      :class="modalTheme"
                    />
                  </b-form-group>
                  <b-form-group label="Identifies As:" label-for="demoIdentifiesAs" label-cols="4" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
                    <b-form-input id="demoIdentifiesAs" v-model="form.demoIdentifiesAs" type="text" size="sm" :class="modalTheme" />
                  </b-form-group>
                  <b-form-group label-cols="4" class="mb-2" :class="modalTheme">
                    <b-form-checkbox id="demoShowLGBTFields" v-model="form.demoShowLGBTFields" size="sm" :class="modalTheme"> Show LGBT Fields </b-form-checkbox>
                  </b-form-group>
                </div>
              </b-col>
              <b-col cols="6">
                <div class="form-row inline-form">
                  <b-form-group label="Sexual Orientation:" label-for="demoSexualOrientation" label-cols="4" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
                    <b-form-select
                      id="demoSexualOrientation"
                      v-model="form.demoSexualOrientation"
                      size="sm"
                      :options="[
                        { value: '', text: 'Select Orientation' },
                        { value: 'heterosexual', text: 'Heterosexual' },
                        { value: 'gay', text: 'Gay' },
                        { value: 'lesbian', text: 'Lesbian' },
                        { value: 'bisexual', text: 'Bisexual' },
                        { value: 'pansexual', text: 'Pansexual' },
                        { value: 'asexual', text: 'Asexual' },
                        { value: 'queer', text: 'Queer' },
                        { value: 'questioning', text: 'Questioning' },
                        { value: 'other', text: 'Other' },
                        { value: 'prefer_not_to_say', text: 'Prefer not to say' },
                      ]"
                      :class="modalTheme"
                    />
                  </b-form-group>

                  <b-form-group label="Marital Status:" label-for="demoMaritalStatus" label-cols="4" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
                    <b-form-select
                      id="demoMaritalStatus"
                      v-model="form.demoMaritalStatus"
                      size="sm"
                      :options="[
                        { value: '', text: 'Select Status' },
                        { value: 'never_married', text: 'Never Married' },
                        { value: 'married', text: 'Married' },
                        { value: 'divorced', text: 'Divorced' },
                        { value: 'widowed', text: 'Widowed' },
                        { value: 'separated', text: 'Separated' },
                        { value: 'domestic_partner', text: 'Domestic Partner' },
                        { value: 'civil_union', text: 'Civil Union' },
                      ]"
                      :class="modalTheme"
                    />
                  </b-form-group>
                  <b-form-group label="Race:" label-for="demoRace" label-cols="4" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
                    <b-form-input id="demoRace" v-model="form.demoRace" type="text" size="sm" :class="modalTheme" />
                  </b-form-group>
                </div>
                <div class="form-row inline-form">
                  <b-form-group label="Ethnicity:" label-for="demoEthnicity" label-cols="4" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
                    <b-form-input id="demoEthnicity" v-model="form.demoEthnicity" type="text" size="sm" :class="modalTheme" />
                  </b-form-group>
                  <b-form-group label="Language:" label-for="demoLanguage" label-cols="4" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
                    <b-form-input id="demoLanguage" v-model="form.demoLanguage" type="text" size="sm" :class="modalTheme" />
                  </b-form-group>
                  <b-form-group label="Birth Place:" label-for="demoBirthPlace" label-cols="4" label-size="sm" label-align-sm="right" class="mb-2" :class="modalTheme">
                    <b-form-input id="demoBirthPlace" v-model="form.demoBirthPlace" type="text" size="sm" :class="modalTheme" />
                  </b-form-group>
                </div>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="6"> </b-col>
              <b-col cols="6"> </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>
      <pre style="color: cyan">{{ form }}</pre>
      <br />
      <hr />

      <div class="form-actions">
        <b-button type="reset" variant="secondary">Reset</b-button>
        <b-button type="submit" variant="primary">Save Patient</b-button>
      </div>
    </b-form>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from "vue";
import moment from "moment";

// * STOREs
import { useStoreUser } from "@/stores/userStore";
const userStore = useStoreUser();
// * STORE
import { usePatientStore } from "@/stores/patientStore";
const patientStore = usePatientStore();

const modalTheme = computed(() => userStore.bModalTheme);

// Emits
const emit = defineEmits(["save-patient", "reset-form"]);

// & PROPS
const props = defineProps({
  testPatient: {
    Type: Object,
    Default: () => {},
  },
});

// Create form factory function
function createForm() {
  return {
    legalTitle: "",
    legalFirstName: "",
    legalMiddleName: "",
    legalLastName: "",
    legalSuffix: "",
    idPatientNumber: 0,
    idTraining: false,
    idSocialSecurityNumber: "",
    idActive: false,
    idReferredBy: "",
    idUsualCaregiver: "",
    privacyLevel: "",
    privacyConfidentialityLevel: "",
    demoDateOfBirth: "05/13/1952",
    demoDateOfBirthYearsAgo: "",
    demoHasTimeOfBirth: false,
    demoTimeOfBirth: "",
    demoSingleMultiBirth: "",
    demoBirthOrder: 1,
    demoBirthSex: "",
    demoIdentifiesAs: "",
    demoShowLGBTFields: false,
    demoSexualOrientation: "",
    demoMaritalStatus: "",
    demoRace: "",
    demoEthnicity: "",
    demoLanguage: "",
    demoBirthPlace: "",
      homeAddress1: "",
    homeAddress2: "",
    homeCounty: "",
    homeCity: "",
    homeState: "",
    homeCountry: "",
    homeZipCode: "",
    homeTelephoneNumber: "",
    homeWorkNumber: "",
    homeMobileNumber: "",
    homeOtherNumber: "",
    homeTelephoneNumberCanText: false,
    homeWorkNumberCanText: false,
    homeMobileNumberCanText: false,
    homeOtherNumberCanText: false,
    homeFaxNumber: "",
    homePagerNumber: "",
    homeEmailAdress: "",
  };
}

// Form state
const form = ref(createForm());
const AreIdsShown = ref(true);
const showFullSSN = ref(false);

const ssnDisplay = ref("");
const ssnRaw = ref("");

const ssnValidationState = computed(() => {
  // Return null if empty (no validation styling)
  if (ssnDisplay.value === "" || ssnRaw.value === "") return null;
  // Return true if valid (green), false if invalid (red)
  return ssnRaw.value.length === 9;
});

const handleSsnInput = (value) => {
  // Remove all non-numeric characters
  const numbersOnly = value.replace(/[^0-9]/g, "");

  // Limit to 9 digits
  const limitedNumbers = numbersOnly.slice(0, 9);

  // Store raw value
  ssnRaw.value = limitedNumbers;
  form.value.idSocialSecurityNumber = limitedNumbers;

  // Format for display
  let formatted = "";
  if (limitedNumbers.length > 0) {
    formatted = limitedNumbers;
    if (limitedNumbers.length > 3) {
      formatted = limitedNumbers.slice(0, 3) + "-" + limitedNumbers.slice(3);
    }
    if (limitedNumbers.length > 5) {
      formatted = formatted.slice(0, 6) + "-" + formatted.slice(6);
    }
  }

  // Always show formatted during typing, don't mask during input
  ssnDisplay.value = formatted;
};

const maskSsnDisplay = () => {
  if (!showFullSSN.value && ssnRaw.value.length >= 4) {
    const lastFour = ssnRaw.value.slice(-4);
    ssnDisplay.value = `###-##-${lastFour}`;
  }
};

const showFullSsnOnFocus = () => {
  if (ssnRaw.value.length > 0) {
    let formatted = ssnRaw.value;
    if (ssnRaw.value.length > 3) {
      formatted = ssnRaw.value.slice(0, 3) + "-" + ssnRaw.value.slice(3);
    }
    if (ssnRaw.value.length > 5) {
      formatted = formatted.slice(0, 6) + "-" + formatted.slice(6);
    }
    ssnDisplay.value = formatted;
  }
};

const toggleSsnVisibility = () => {
  showFullSSN.value = !showFullSSN.value;

  if (showFullSSN.value) {
    showFullSsnOnFocus();
  } else {
    maskSsnDisplay();
  }
};

// Add watcher for demoDateOfBirth to update demoDateOfBirthYearsAgo
watch(
  () => form.value.demoDateOfBirth,
  (newVal) => {
    if (newVal) {
      form.value.demoDateOfBirthYearsAgo = moment(newVal).fromNow();
    } else {
      form.value.demoDateOfBirthYearsAgo = "";
    }
  }
);

// Form validation
const isFormValid = () => {
  // if (!form.value.firstName.trim()) {
  //   alert("First name is required");
  //   return false;
  // }
  // if (!form.value.lastName.trim()) {
  //   alert("Last name is required");
  //   return false;
  // }
  // if (!form.value.patientID.trim()) {
  //   alert("Patient ID is required");
  //   return false;
  // }
  // return true;
};

// Form handlers
function onSubmit() {
  if (!isFormValid()) {
    return;
  }

  const payload = {
    ...form.value,
    // Convert date strings to proper formats if needed
    // chartReviewDate: form.value.chartReviewDate ? new Date(form.value.chartReviewDate).toISOString() : null,
    // startDate: form.value.startDate ? new Date(form.value.startDate).toISOString() : null,
    // endDate: form.value.endDate ? new Date(form.value.endDate).toISOString() : null,
    // nextAppointment: form.value.nextAppointment ? new Date(form.value.nextAppointment).toISOString() : null,
    // serviceDate: form.value.serviceDate ? moment(form.value.serviceDate).format("MM/DD/YYYY") : "",
  };

  emit("save-patient", payload);
  console.log("Saving patient:", payload);
}

function onReset() {
  ssnDisplay.value = "";
  ssnRaw.value = "";

  form.value = createForm();
  emit("reset-form");
}

// Load initial data (example with the provided values)
function loadSampleData() {
  form.value = {
    legalTitle: "Mr.",
    legalFirstName: "Heywood",
    legalMiddleName: "J.",
    legalLastName: "BleuMei",
    legalSuffix: "Jr",
    idPatientNumber: 0,
    idTraining: false,
    idSocialSecurityNumber: "111121234",
    idActive: true,
    idReferredBy: 1,
    idUsualCaregiver: 1,
    privacyLevel: 2,
    privacyConfidentialityLevel: 2,
    demoDateOfBirth: "1952-05-13",
    demoDateOfBirthYearsAgo: "",
    demoHasTimeOfBirth: false,
    demoTimeOfBirth: "",
    demoSingleMultiBirth: "",
    demoBirthOrder: 0,
    demoBirthSex: "",
    demoIdentifiesAs: "",
    demoShowLGBTFields: false,
    demoSexualOrientation: "",
    demoMaritalStatus: "",
    demoRace: "",
    demoEthnicity: "",
    demoLanguage: "",
    demoBirthPlace: "",
        homeAddress1: "Rural Route #1",
    homeAddress2: "Box #168",
    homeCounty: "Jefferson County",
    homeCity: "Fenton",
    homeState: "Missouri",
    homeCountry: "USA",
    homeZipCode: "63026",
    hStartDate: "2026-01-01",
    hEndDate: "2027-01-01",
    homeTelephoneNumber: "3141111234",
    homeWorkNumber: "3142221234",
    homeMobileNumber: "3143331234",
    homeOtherNumber: "4441116543",
    homeTelephoneNumberCanText: true,
    homeWorkNumberCanText: false,
    homeMobileNumberCanText: true,
    homeOtherNumberCanText: false,
    homeFaxNumber: "5559999999",
    homePagerNumber: "5555112222",
    homeEmailAdress: "homeEmailAddress@email.com",

    // personID: 14526,
    // firstName: "Sample",
    // middleName: "M",
    // lastName: "DATA",
    // dateOfBirth: "1971-09-16",
    // patientID: "2405061",
    // memberID: "N/A",
    // insuranceCarrier: "UNKNOWN",
    // dob: "19710916",
    // gender: "F",
    // providerName: "Grizzle, Melissa",
    // serviceDate: "2022-12-14",
    // lastSeenByMe: "Grizzle, Melissa",
    // chartReviewDate: moment("0001-01-01T00:00:00Z").isValid() ? moment("0001-01-01T00:00:00Z").format("YYYY-MM-DDTHH:mm") : moment().format("YYYY-MM-DDTHH:mm"),
    // chartReviewBy: null,
    // chartReviewNeeded: true,
    // chartReviewComment: null,
    // chartReviewRole: null,
    // emrId: null,
    // startDate: moment("1900-01-01T00:00:00Z").format("YYYY-MM-DDTHH:mm"),
    // nextAppointmentWith: "",
    // nextAppointmentReason: "",
    // nextAppointmentType: "",
    // nextAppointmentComment: "",
    // patientStatus: true,
    // endDate: moment("1900-01-01T00:00:00Z").format("YYYY-MM-DDTHH:mm"),
    // appointmentLocation: null,
    // isScheduled: false,
    // nextAppointment: moment("1900-01-01T00:00:00Z").format("YYYY-MM-DDTHH:mm"),
  };
  // Update SSN display
  ssnRaw.value = form.value.idSocialSecurityNumber;
  handleSsnInput(form.value.idSocialSecurityNumber);
}
function loadFromProps() {
  if (props.testPatient && typeof props.testPatient === "object") {
    form.value = {
      ...createForm(),
      ...props.testPatient,
      chartReviewDate: props.testPatient.chartReviewDate ? moment(props.testPatient.chartReviewDate).format("YYYY-MM-DDTHH:mm") : moment().format("YYYY-MM-DDTHH:mm"),
      startDate: props.testPatient.startDate ? moment(props.testPatient.startDate).format("YYYY-MM-DDTHH:mm") : moment("1900-01-01").format("YYYY-MM-DDTHH:mm"),
      endDate: props.testPatient.endDate ? moment(props.testPatient.endDate).format("YYYY-MM-DDTHH:mm") : moment("1900-01-01").format("YYYY-MM-DDTHH:mm"),
      nextAppointment: props.testPatient.nextAppointment ? moment(props.testPatient.nextAppointment).format("YYYY-MM-DDTHH:mm") : moment("1900-01-01").format("YYYY-MM-DDTHH:mm"),
    };
  }
}

// New method to bind from store object to form
function loadFromPatientStore() {
  const currentPatient = patientStore.currentPatient;
  if (currentPatient && typeof currentPatient === "object") {
    form.value = {
      ...createForm(),
      ...currentPatient,
      // Handle date conversions
      dateOfBirth: currentPatient.dateOfBirth ? moment(currentPatient.dateOfBirth).format("YYYY-MM-DD") : "",
      serviceDate: currentPatient.serviceDate ? moment(currentPatient.serviceDate).format("YYYY-MM-DD") : "",
      chartReviewDate: currentPatient.chartReviewDate ? moment(currentPatient.chartReviewDate).format("YYYY-MM-DDTHH:mm") : moment().format("YYYY-MM-DDTHH:mm"),
      startDate: currentPatient.startDate ? moment(currentPatient.startDate).format("YYYY-MM-DDTHH:mm") : moment("1900-01-01").format("YYYY-MM-DDTHH:mm"),
      endDate: currentPatient.endDate ? moment(currentPatient.endDate).format("YYYY-MM-DDTHH:mm") : moment("1900-01-01").format("YYYY-MM-DDTHH:mm"),
      nextAppointment: currentPatient.nextAppointment ? moment(currentPatient.nextAppointment).format("YYYY-MM-DDTHH:mm") : moment("1900-01-01").format("YYYY-MM-DDTHH:mm"),
    };
    console.log("Loaded patient from store:", currentPatient);
  } else {
    console.warn("No current patient found in store");
    alert("No current patient available in store");
  }
}

onMounted(() => {
  // console.log("onMounted() : Patient Form >>>>");
  // console.log("props : testPatient ");
  // console.log(props.testPatient);
  // loadSampleData();
  // loadFromProps();
  // loadFromPatientStore();
});
</script>

<style scoped>
.patient-form-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  height: 65vh;
  overflow: hidden;
  overflow-y: auto;
}

.form-section {
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  text-align: center;
}

.form-section h3 {
  margin-bottom: 15px;
  font-size: 1.2em;
  border-bottom: 2px solid #007bff;
  padding-bottom: 5px;
  text-align: center;
}

.form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0px;
  margin-bottom: 15px;
  justify-content: stretch;
  width: 100%;
}

.form-group {
  flex: 1;
  min-width: 250px;
}

.form-group.full-width {
  flex: 100%;
}

.inline-form {
  align-items: flex-end;
  justify-content: stretch;
  width: 100%;
}

.inline-group {
  display: flex;
  align-items: center;
  gap: 0px;
  min-width: 0;
  flex: 1 1 auto;
  text-align: left;
}

.inline-group label {
  margin-bottom: 0;
  white-space: nowrap;
  min-width: 80px;
  font-size: 10px;
  text-align: right;
}

.inline-group .form-control {
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
}

.form-group.checkbox-group {
  display: flex;
  align-items: center;
  min-width: auto;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
}

.checkbox-group label {
  display: flex;
  align-items: center;
  gap: 0px;
  margin-bottom: 0;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control:focus {
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.form-control:disabled {
  background-color: #e9ecef;
  opacity: 1;
}

textarea.form-control {
  resize: vertical;
  min-height: 60px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 20px 0;
  border-top: 1px solid #dee2e6;
  margin-top: 20px;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.15s ease-in-out;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-primary:hover {
  background-color: #0056b3;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #545b62;
}

.btn-info {
  background-color: #17a2b8;
  color: white;
}

.btn-info:hover {
  background-color: #138496;
}

/* Bootstrap Vue inline form adjustments */
.form-row .form-group {
  margin-bottom: 0.5rem;
}

/* Compact spacing for inline forms - Remove gaps */
.inline-form .form-group {
  margin-bottom: 0.5rem;
}

/* Label styling for inline forms - Right justified, no gaps */
.form-group label.col-form-label {
  font-size: 15px !important;
  font-weight: 600;
  padding-right: 0 !important;
  padding-left: 0 !important;
  margin-right: 0 !important;
  text-align: right !important;
  justify-content: flex-end !important;
  white-space: nowrap;
  display: flex !important;
  align-items: center !important;
}

/* Ensure all labels in form groups align right with no gaps */
.form-group .col-form-label {
  font-size: 15px !important;
  font-weight: 600;
  text-align: right !important;
  display: flex !important;
  align-items: center !important;
  justify-content: flex-end !important;
  padding-right: 0 !important;
  padding-left: 0 !important;
  margin-right: 0 !important;
  white-space: nowrap;
}

/* Bootstrap Vue form group label alignment - no gaps */
.form-group .col-form-label-sm {
  font-size: 15px !important;
  font-weight: 600;
  text-align: right !important;
  justify-content: flex-end !important;
  padding-right: 0 !important;
  padding-left: 0 !important;
  margin-right: 0 !important;
}

/* Override Bootstrap Vue default label alignment - remove all gaps */
.form-group .row .col-form-label,
.b-form-group .col-form-label {
  font-size: 15px !important;
  font-weight: 600;
  text-align: right !important;
  justify-content: flex-end !important;
  padding-right: 0 !important;
  padding-left: 0 !important;
  margin-right: 0 !important;
  white-space: nowrap;
}

/* Additional specificity for Bootstrap Vue components - no gaps */
.b-form-group label.col-form-label {
  font-size: 15px !important;
  text-align: right !important;
  justify-content: flex-end !important;
  padding-right: 0 !important;
  padding-left: 0 !important;
  margin-right: 0 !important;
}

/* Remove Bootstrap default row gutters and spacing */
.b-form-group .row {
  margin-left: 0 !important;
  margin-right: 0 !important;
}

.b-form-group .row > [class*="col"] {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

/* Remove gaps between labels and inputs in all sections */
.form-section .row {
  margin-left: 0 !important;
  margin-right: 0 !important;
}

.form-section .row > [class*="col"] {
  padding-left: 5px !important;
  padding-right: 5px !important;
}

/* Remove Bootstrap Vue form group internal spacing */
.b-form-group {
  margin-bottom: 0.5rem !important;
}

.b-form-group .row {
  margin: 0 !important;
}

/* Specific targeting for identification, privacy sections */
.identification-section .row,
.form-section .row {
  gap: 0 !important;
  margin: 0 !important;
}

.identification-section [class*="col"],
.form-section [class*="col"] {
  padding-left: 5px !important;
  padding-right: 5px !important;
}

/* 10px gap between label and input columns */
.form-section .row > [class*="col"]:first-child {
  padding-right: 10px !important;
  padding-left: 0 !important;
}

.form-section .row > [class*="col"]:not(:first-child) {
  padding-left: 0 !important;
  padding-right: 5px !important;
}

/* Label styling for inline Privacy section */
.form-section label {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  padding: 0 !important;
  margin: 0 !important;
}

.ssn-input-container {
  position: relative;
  display: flex;
  align-items: center;
}

.ssn-toggle-btn {
  position: absolute;
  right: 2px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  padding: 2px 6px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 26px;
}

.ssn-toggle-btn:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.ssn-input-container .form-control {
  padding-right: 35px;
}

</style>
